/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/ban-types */
import React, { useEffect, useMemo } from 'react';
import { createContext, PropsWithChildren, useCallback, useState } from 'react';
import { usePhone } from '../use-phone';
import { usePhoneLock } from '../use-phone-lock/use-phone-lock';
import { CallVendor, WebPhoneState } from '../use-phone/provider';
import _, { debounce } from 'lodash';
import { usePhoneWarnings } from '../use-phone/use-phone-warning';
import { useDesktop } from '@revenuewell/logic-desktop';
import { normalizeUSPhoneNumber } from '../../utils/formatter';
import { getDTMFforKey } from '../../utils/webphone';
import { IPhoneActions, StartParams } from './types';
import useScreenPop from '../use-screen-pop/use-screen-pop';

const libwebphone = (window as any).libwebphone;
let libwebphoneInstance: any;

function getConfig(server: string) {
  return {
    dialpad: {
      globalKeyShortcuts: false,
      renderTargets: []
    },
    callList: { renderTargets: [] },
    callControl: { renderTargets: [] },
    mediaDevices: { videoinput: { enabled: false } },
    audioContext: { renderTargets: [] },
    videoCanvas: { renderTargets: [] },
    userAgent: {
      renderTargets: [],
      transport: {
        sockets: [server]
      }
    }
  };
}

export function findCall(id: string) {
  const calls = libwebphoneInstance?.getCallList().getCalls();
  return _(calls).find((x) => x.getId() === id);
}

function updateState(updatePhoneState: (webPhoneState: WebPhoneState) => Promise<void>) {
  const callList = libwebphoneInstance?.getCallList().getCalls() || [];
  const calls = callList.map((call: any) => ({
    ...call.summary(),
    remoteIdentityUser: call.remoteIdentity(true)?.uri?.user
  })) as CallVendor[];

  updatePhoneState({
    calls,
    isReady: libwebphoneInstance?.getUserAgent().isReady()
  });
}

const updateStateDebounced = debounce(
  (updatePhoneState: (webPhoneState: WebPhoneState) => Promise<void>) => updateState(updatePhoneState),
  20
);

export function isStarted() {
  return !!libwebphoneInstance;
}

const excludePrefixEvents = ['call.primary.update'];
const excludeSuffixEvents = ['.timeupdate', '.rendered'];

export const PhoneActionsContext = createContext<IPhoneActions>(null!);

export default function PhoneActionsProvider({ children }: PropsWithChildren<{}>) {
  const {
    clientId,
    updatePhoneState,
    deviceControl,
    webPhoneStatus,
    ignoreCall: ignorePhoneCall,
    setMicrophoneExists,
    lockDevice,
    callsRef
  } = usePhone();
  const [hasInstance, setHasInstance] = useState(false);
  const { handleStartCall, warning } = usePhoneWarnings();
  const { isDesktopApplication, isDesktopWidget, startCall } = useDesktop();
  const { finalizePhoneLock, deviceLockCredentials } = usePhoneLock();
  const { ignoreCall: ignoreScreenPop } = useScreenPop();

  useEffect(() => {
    if (isDesktopApplication() && !isDesktopWidget) return;

    if (hasInstance && libwebphoneInstance) {
      const handleOnAny = async (eventName: string) => {
        if (eventName === 'userAgent.connected' && deviceControl.deviceId) {
          console.log("PHONE -> kazoo 'userAgent.connected' event");
          await finalizePhoneLock(deviceControl.deviceId, clientId);
        }

        if (eventName === 'userAgent.disconnected') {
          console.log("PHONE -> kazoo 'userAgent.disconnected' event");
          await stop();
          if (deviceControl.deviceId) await lockDevice();
        }

        if (
          !excludePrefixEvents.find((excl) => eventName.startsWith(excl)) &&
          !excludeSuffixEvents.find((excl) => eventName.endsWith(excl))
        ) {
          updateStateDebounced(updatePhoneState);
        }
      };

      libwebphoneInstance?.onAny(handleOnAny);

      return () => libwebphoneInstance?.offAny(handleOnAny);
    }
  }, [hasInstance, updatePhoneState, lockDevice, deviceControl, finalizePhoneLock]);

  useEffect(() => {
    if (deviceControl.deviceStatus === 'ready') refreshAudioInput();
  }, [webPhoneStatus.microphoneExists, deviceControl]);

  const stop = async () => {
    if (libwebphoneInstance) {
      libwebphoneInstance.offAny();
      libwebphoneInstance?.getUserAgent().stop();
      // libwebphone.stop() request is not async function but in the core an async process,
      // so give 500ms to perform the request
      await new Promise((resolve) => setTimeout(resolve, 500));
      setHasInstance(false);
      libwebphoneInstance = null;
    }
  };

  const start = async (params: StartParams) => {
    if (libwebphoneInstance) return;

    const config = getConfig(params.server);
    libwebphoneInstance = new libwebphone(config);
    setHasInstance(true);

    const { auth } = params;
    libwebphoneInstance?.getUserAgent().start(auth.username, auth.password, auth.realm);

    await new Promise((resolve) => setTimeout(resolve, 1000));
  };

  const restart = async () => {
    if (deviceLockCredentials) {
      await stop();
      await start({
        server: deviceLockCredentials.sipWss,
        auth: {
          username: deviceLockCredentials.sipUserName,
          password: deviceLockCredentials.sipPassword,
          realm: deviceLockCredentials.sipRealm
        }
      });
    }
  };

  const call = useCallback(
    (number: string) => {
      if (isPhoneSessionExists(number)) return;

      if (isDesktopApplication() && !isDesktopWidget) {
        startCall(number);
      } else {
        handleStartCall(number);
        if (!warning) libwebphoneInstance?.getUserAgent().call(normalizeUSPhoneNumber(number));
      }
    },
    [handleStartCall, libwebphoneInstance]
  );

  const refreshAudioInput = async () => {
    if (!libwebphoneInstance || !navigator?.mediaDevices) return;

    const devices = await navigator.mediaDevices.enumerateDevices();
    const microphones = devices.filter((device) => device.kind === 'audioinput');

    if (microphones.length > 0) restart();
  };

  const transfer = useCallback(({ id, number }: { id: string; number: string }) => {
    findCall(id)?.transfer(normalizeUSPhoneNumber(number));
  }, []);

  const hangupAll = useCallback(() => {
    libwebphoneInstance?.getUserAgent().hangupAll();
  }, []);

  const answer = useCallback((id: string) => {
    markScreenPopAsIgnored(id);
    findCall(id)?.answer();
    libwebphoneInstance?.getCallList().switchCall(id);
  }, []);

  const reject = useCallback((id: string) => {
    markScreenPopAsIgnored(id);
    findCall(id)?.reject();
  }, []);

  const mute = useCallback((id: string) => {
    findCall(id)?.mute();
  }, []);

  const cancel = useCallback((id: string) => {
    markScreenPopAsIgnored(id);
    findCall(id)?.cancel();
  }, []);

  const hangup = useCallback((id: string) => {
    markScreenPopAsIgnored(id);
    findCall(id)?.hangup();
  }, []);

  const ignoreCall = useCallback(
    async (id: string) => {
      markScreenPopAsIgnored(id);
      libwebphoneInstance?.getAudioContext().stopRinging(id);
      ignorePhoneCall(id);
      updateStateDebounced(updatePhoneState);
    },
    [ignorePhoneCall, updatePhoneState]
  );

  const unmute = useCallback((id: string) => {
    findCall(id)?.unmute();
  }, []);

  const hold = useCallback((id: string) => {
    findCall(id)?.hold();
  }, []);

  const unhold = useCallback((id: string) => {
    findCall(id)?.unhold();
    if (!findCall(id).isPrimary()) libwebphoneInstance?.getCallList().switchCall(id);
  }, []);

  const sendDTMF = useCallback((id: string, char: string) => {
    findCall(id)?.sendDTMF(char);
  }, []);

  const setRingerEnabled = useCallback(async (isEnabled: boolean) => {
    libwebphoneInstance?.getAudioContext().changeVolume('ringer', isEnabled ? 1 : 0);
  }, []);

  const playTones = useCallback(async (key: string) => {
    const toneFreqs = getDTMFforKey(key).split(',');
    if (toneFreqs.length > 1) {
      libwebphoneInstance?.getAudioContext().playTones(parseInt(toneFreqs[0]), parseInt(toneFreqs[1]));
    }
  }, []);

  const microphoneExists = useCallback(async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const microphone = devices.find((device) => device.kind === 'audioinput');
    setMicrophoneExists(!!microphone);
  }, [setMicrophoneExists]);

  const isPhoneSessionExists = (phoneNmber: string): boolean => {
    const calls = libwebphoneInstance?.getCallList().getCalls();

    if (!calls) return false;

    const activeCalls = calls.filter((c: any) => c._session != null).map((c: any) => c._session);
    return activeCalls.some((c: any) => c._remote_identity?._uri?._user === phoneNmber);
  };

  const markScreenPopAsIgnored = (id: string) => {
    const call = Object.values(callsRef.current.allCalls).find((x) => x.callId === id);
    if (call && call.remoteIdentity) ignoreScreenPop(call.remoteIdentity);
  };

  const context = useMemo(
    () => ({
      stop,
      start,
      call,
      transfer,
      hangupAll,
      answer,
      reject,
      mute,
      cancel,
      hangup,
      ignoreCall,
      unmute,
      hold,
      unhold,
      sendDTMF,
      setRingerEnabled,
      playTones,
      microphoneExists,
      refreshAudioInput
    }),
    [
      stop,
      start,
      call,
      transfer,
      hangupAll,
      answer,
      reject,
      mute,
      cancel,
      hangup,
      ignoreCall,
      unmute,
      hold,
      unhold,
      sendDTMF,
      setRingerEnabled,
      playTones,
      microphoneExists,
      refreshAudioInput
    ]
  );

  return <PhoneActionsContext.Provider value={context}>{children}</PhoneActionsContext.Provider>;
}
